import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import DataTable from "react-data-table-component";
import { post } from "../../services/api";
import { toast } from "react-toastify";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function MachineAttendanceRecord() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tokenData, setTokenData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: {
      beginTime: "2025-10-01T09:00",
      endTime: "2025-10-31T18:00",
      pageIndex: 1,
      pageSize: 20,
    },
  });

  // Get token from HikConnect API
  const getToken = async () => {
    try {
      const response = await post("HikConnect/get-token", {});
      if (response.accessToken) {
        setTokenData(response);
        localStorage.setItem("hikToken", response.accessToken);
        localStorage.setItem("tokenExpireTime", response.expireTime);
        return response.accessToken;
      }
    } catch (error) {
      console.error("Error fetching token:", error);
      toast.error("Failed to get authentication token");
      return null;
    }
  };

  // Get attendance report
  const getAttendanceReport = async (formData) => {
    setLoading(true);
    try {
      let token = tokenData?.accessToken || localStorage.getItem("hikToken");

      // If no token or token might be expired, get a new one
      if (!token) {
        token = await getToken();
      }

      if (!token) {
        toast.error("No authentication token available");
        setLoading(false);
        return;
      }

      const requestBody = {
        pageIndex: formData.pageIndex || 1,
        pageSize: formData.pageSize || 20,
        beginTime: formData.beginTime,
        endTime: formData.endTime,
      };

      const response = await post("Attendance/get-report", requestBody, {
        headers: {
          Token: token, // ✅ Correct header name
          "Content-Type": "application/json",
        },
      });

      if (
        response &&
        response.reportDataList &&
        Array.isArray(response.reportDataList)
      ) {
        setAttendanceData(response.reportDataList);
        toast.success(
          `Loaded ${response.reportDataList.length} attendance records`
        );
      } else {
        setAttendanceData([]);
        toast.info("No attendance records found for the selected period");
      }
    } catch (error) {
      console.error("Error fetching attendance report:", error);

      // If token might be expired, try to refresh it
      if (error.response?.status === 401) {
        toast.info("Token expired, refreshing...");
        const newToken = await getToken();
        if (newToken) {
          // Retry the request with new token
          await getAttendanceReport(formData);
          return;
        }
      }

      toast.error("Failed to fetch attendance records");
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  // Load token on component mount
  useEffect(() => {
    getToken();
  }, []);

  const handleSearch = (data) => {
    getAttendanceReport(data);
  };

  const handleReset = () => {
    reset({
      beginTime: "2025-10-01T09:00",
      endTime: "2025-10-31T18:00",
      pageIndex: 1,
      pageSize: 20,
    });
    setAttendanceData([]);
  };

  // Helper function to get weekday name
  const getWeekdayName = (weekday) => {
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return weekdays[weekday - 1] || "Unknown";
  };

  // Helper function to get attendance status text
  const getAttendanceStatus = (status) => {
    const statusMap = {
      1: "Present",
      2: "Absent",
      3: "Late",
      4: "Early Leave",
      5: "Absence", // Based on your data
      6: "Leave",
      7: "Holiday",
    };
    return statusMap[status] || `Status ${status}`;
  };

  const columns = [
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "80px",
    },
    {
      name: "Employee Name",
      selector: (row) => row.fullName,
      sortable: true,
      width: "150px",
    },
    {
      name: "Person Code",
      selector: (row) => row.personCode,
      sortable: true,
      width: "120px",
    },
    {
      name: "Date",
      selector: (row) => row.date,
      sortable: true,
      width: "120px",
    },
    {
      name: "Day",
      selector: (row) => getWeekdayName(row.weekday),
      sortable: true,
      width: "100px",
    },
    {
      name: "Shift",
      selector: (row) => row.timetableName,
      sortable: true,
      width: "120px",
    },
    {
      name: "Scheduled Time",
      cell: (row) => (
        <div>
          <div>In: {row.checkInTime}</div>
          <div>Out: {row.checkOutTime}</div>
        </div>
      ),
      width: "120px",
    },
    {
      name: "Actual Clock",
      cell: (row) => (
        <div>
          <div>In: {row.clockInTime || "No Record"}</div>
          <div>Out: {row.clockOutTime || "No Record"}</div>
        </div>
      ),
      width: "140px",
    },
    {
      name: "Status",
      selector: (row) => getAttendanceStatus(row.attendanceStatus),
      sortable: true,
      width: "120px",
      cell: (row) => {
        const status = getAttendanceStatus(row.attendanceStatus);
        const getStatusColor = () => {
          switch (row.attendanceStatus) {
            case 1:
              return "bg-success"; // Present
            case 2:
            case 5:
              return "bg-danger"; // Absent/Absence
            case 3:
              return "bg-warning"; // Late
            case 4:
              return "bg-info"; // Early Leave
            case 6:
              return "bg-primary"; // Leave
            case 7:
              return "bg-secondary"; // Holiday
            default:
              return "bg-secondary";
          }
        };

        return (
          <span className={`badge ${getStatusColor()} text-white`}>
            {status}
          </span>
        );
      },
    },
    {
      name: "Work Duration",
      selector: (row) => row.workDuration,
      sortable: true,
      width: "120px",
    },
    {
      name: "Late Duration",
      selector: (row) => row.lateDuration,
      sortable: true,
      width: "120px",
    },
    {
      name: "Early Duration",
      selector: (row) => row.earlyDuration,
      sortable: true,
      width: "120px",
    },
  ];

  const exportToExcel = () => {
    if (attendanceData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const filteredData = attendanceData.map((row, index) => ({
      "Sr No": index + 1,
      "Employee Name": row.fullName,
      "Person Code": row.personCode,
      Date: row.date,
      Day: getWeekdayName(row.weekday),
      Shift: row.timetableName,
      "Scheduled In": row.checkInTime,
      "Scheduled Out": row.checkOutTime,
      "Actual Clock In": row.clockInTime || "No Record",
      "Actual Clock Out": row.clockOutTime || "No Record",
      Status: getAttendanceStatus(row.attendanceStatus),
      "Work Duration": row.workDuration,
      "Absence Duration": row.absenceDuration,
      "Late Duration": row.lateDuration,
      "Early Duration": row.earlyDuration,
      "Break Duration": row.breakDuration,
      "Leave Duration": row.leaveDuration,
      "Overtime Duration": row.overtimeDuration,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Records");
    XLSX.writeFile(workbook, "MachineAttendanceRecords.xlsx");
    toast.success("Data exported to Excel successfully");
  };

  const exportToPDF = () => {
    if (attendanceData.length === 0) {
      toast.warning("No data to export");
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    // Add logo if available
    if (logo) {
      doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);
    }

    const title = "Machine Attendance Records";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logo ? logoY + logoHeight + 10 : 20;
    doc.text(title, titleX, titleY);

    const headers = [
      "Sr No",
      "Employee Name",
      "Person Code",
      "Date",
      "Day",
      "Shift",
      "Scheduled In",
      "Scheduled Out",
      "Status",
      "Work Duration",
    ];

    const bodyData = attendanceData.map((row, index) => {
      return [
        index + 1,
        row.fullName,
        row.personCode,
        row.date,
        getWeekdayName(row.weekday),
        row.timetableName,
        row.checkInTime,
        row.checkOutTime,
        getAttendanceStatus(row.attendanceStatus),
        row.workDuration,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
    });

    doc.save("MachineAttendanceRecords.pdf");
    toast.success("Data exported to PDF successfully");
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Machine Attendance Records</h2>
      </div>

      <div className="row g-3">
        {/* Search Form */}
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Search Criteria</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit(handleSearch)}>
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label htmlFor="beginTime" className="form-label">
                      Start Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control ${
                        errors.beginTime ? "is-invalid" : ""
                      }`}
                      id="beginTime"
                      {...register("beginTime", {
                        required: "Start date is required",
                      })}
                    />
                    {errors.beginTime && (
                      <div className="invalid-feedback">
                        {errors.beginTime.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="endTime" className="form-label">
                      End Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      className={`form-control ${
                        errors.endTime ? "is-invalid" : ""
                      }`}
                      id="endTime"
                      {...register("endTime", {
                        required: "End date is required",
                      })}
                    />
                    {errors.endTime && (
                      <div className="invalid-feedback">
                        {errors.endTime.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-2">
                    <label htmlFor="pageSize" className="form-label">
                      Page Size
                    </label>
                    <select
                      className="form-select"
                      id="pageSize"
                      {...register("pageSize")}
                    >
                      <option value="20">20</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>

                  <div className="col-md-4">
                    <div className="d-flex gap-2">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-search me-2"></i>
                            Search
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleReset}
                        disabled={loading}
                      >
                        <i className="fas fa-redo me-2"></i>
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="card-title mb-0">
                  Attendance Records{" "}
                  {attendanceData.length > 0 &&
                    `(${attendanceData.length} records)`}
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    data-bs-toggle="tooltip"
                    data-bs-custom-class="custom-tooltip"
                    title="Export to Excel"
                    className="btn btn-outline-success px-3 py-2"
                    onClick={exportToExcel}
                    disabled={attendanceData.length === 0}
                  >
                    <i className="fas fa-file-excel me-2"></i>
                    Excel
                  </button>
                  <button
                    type="button"
                    data-bs-toggle="tooltip"
                    data-bs-custom-class="custom-tooltip"
                    title="Export to PDF"
                    className="btn btn-outline-danger px-3 py-2"
                    onClick={exportToPDF}
                    disabled={attendanceData.length === 0}
                  >
                    <i className="fas fa-file-pdf me-2"></i>
                    PDF
                  </button>
                </div>
              </div>

              {attendanceData.length > 0 ? (
                <div className="table-container">
                  <DataTable
                    columns={columns}
                    data={attendanceData}
                    pagination
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                    dense
                    customStyles={customTableStyles}
                    highlightOnHover={true}
                    onChangePage={setCurrentPage}
                    onChangeRowsPerPage={setRowsPerPage}
                    progressPending={loading}
                  />
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
                  <p className="text-muted">
                    {loading
                      ? "Loading attendance records..."
                      : "No attendance records found. Use the search form above to get records."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MachineAttendanceRecord;
